import { Button } from '@heidi/design-system/button';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import { menuSurfaceVariants } from '@heidi/design-system/styles';
import { cn } from '@heidi/utils';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { type Editor } from '@tiptap/react';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { InlineEditPromptInput } from './inline-selection-edit/inline-edit-prompt-input';
import { selectionAiActionsMessages } from './selection-ai-actions.messages';
import { getVisibleCompactMenuActions } from './selection-ai-actions.utils';
import { useSelectionAiActionHandlers } from './use-selection-ai-action-handlers';

/**
 * The "Ask Heidi" button in the selection toolbar. Its dropdown lists the
 * preset AI actions, then an "Ask to edit" field that sends a typed
 * instruction to the inline edit panel.
 */
export const SelectionHeidiMenu = ({ editor }: { editor: Editor }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const { handleActionClick, handleCustomPrompt } = useSelectionAiActionHandlers({
    editor,
  });

  const actions = getVisibleCompactMenuActions();

  return (
    <Dropdown.Root open={open} onOpenChange={setOpen}>
      <Dropdown.Trigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="selection-variant-heidi-button"
        >
          <HeidiLogoMark aria-hidden className="size-3.5" />
          {intl.formatMessage(selectionAiActionsMessages.aiActions)}
          <ChevronDown />
        </Button>
      </Dropdown.Trigger>
      {/* No portal: the toolbar is already appended to the dialog container, and
          a body portal here would position the menu at (0,0). */}
      <Dropdown.Content
        side="bottom"
        align="start"
        sideOffset={14}
        collisionPadding={8}
        onMouseDown={(e) => e.preventDefault()}
        className={cn(menuSurfaceVariants(), 'z-[60] flex w-60 flex-col gap-0.5')}
        data-testid="selection-variant-heidi-menu"
      >
        {actions.map(({ id, icon: Icon, message }) => (
          <DropdownMenuItem
            key={id}
            onSelect={() => handleActionClick(id)}
            data-testid={`selection-variant-heidi-${id}`}
          >
            <Icon size={14} aria-hidden />
            {intl.formatMessage(message)}
          </DropdownMenuItem>
        ))}
        {/* Stop keydown bubbling so the menu's typeahead doesn't swallow typing. */}
        <div
          className="mt-1 rounded-md border border-outline px-2.5 py-1"
          onKeyDown={(e) => e.stopPropagation()}
          data-testid="selection-variant-heidi-edit"
        >
          <InlineEditPromptInput
            density="compact"
            placeholder={intl.formatMessage(
              selectionAiActionsMessages.editWithAiPlaceholder
            )}
            onSubmit={(question) => {
              setOpen(false);
              handleCustomPrompt(question);
            }}
          />
        </div>
      </Dropdown.Content>
    </Dropdown.Root>
  );
};
